import React, { useState } from "react";
import { PageComponentProps } from "../../interfaces/common";
import styled from "styled-components";
import { pxToEm } from "../../util/helpers";
import constants from "../_globalStyles/constants";
import Icon from "../atoms/Icon";
import Button from "../atoms/Button";
import { useTranslation } from "react-i18next";
import ConfirmationPopup from "../organisms/ConfirmationPopup";

interface ValidateBoxProps extends PageComponentProps {
  serviceProvider?: string | null;
  myMunicipality?: string | boolean;
  unlimitedEntries?: boolean;
  handleValidateTicket: () => void;
  availiableEntries: number | null;
  code?: string | null;
  ticketType?: string;
}

const ValidateBox: React.FunctionComponent<ValidateBoxProps> = ({
  serviceProvider,
  myMunicipality,
  unlimitedEntries,
  handleValidateTicket,
  code,
  availiableEntries,
  ticketType
}) => {
  const [open, setOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  const [t] = useTranslation();

  const ValidateBoxView = styled.div`
    margin: 16px;
    padding: 1em;
    border-radius: 1.5em;
    border: 1px solid ${constants.LINK_COLOR};
    position: relative;
    overflow: hidden;

    .expand-button {
      position: absolute;
      top: 0;
      right: 0;
      border-radius: 1.5em;
      height: ${pxToEm(58, 16)}rem;
      width: ${pxToEm(58, 16)}rem;
      background: none;
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;

      svg {
        width: ${pxToEm(24, 16)}rem;
        height: ${pxToEm(24, 16)}rem;
        ${open && "transform: scale(-1)"}
      }
    }

    .box-header {
      display: flex;
      align-items: center;
      cursor: pointer;

      .emoji {
        font-size: ${pxToEm(24, 16)}rem;
        margin-right: 1rem;
        line-height: 1rem;
      }

      .text {
        margin: 0;
        font-weight: bold;
        color: ${constants.LINK_COLOR};
      }
    }

    .logo {
      height: ${pxToEm(24, 16)}rem;
      margin-bottom: 1em;
      margin-left: 0.5rem;
    }

    .validate-note {
      margin: 0;
      padding: 0 0.5rem;
      line-height: ${pxToEm(22, 16)}rem;
    }

    .validate {
      width: 100%;
      margin: ${pxToEm(12, 16)}rem 0 ${pxToEm(8, 16)}rem;
    }

    .note {
      margin: 0;
      text-align: center;
      span {
        margin-right: 1ch;
      }
    }

    .code {
      text-align: center;
      font-weight: bold;
      font-size: ${pxToEm(32, 16)}rem;
      margin: ${pxToEm(16, 16)}rem 0 ${pxToEm(8, 16)}rem;
      letter-spacing: ${pxToEm(4, 16)}rem;
    }

    .pass {
      margin: ${pxToEm(12, 16)}rem 0 ${pxToEm(8, 16)}rem;
      position: relative;
      height: ${pxToEm(84, 16)}rem;
      display: flex;
      justify-content: center;
      align-items: center;
      p {
        font-weight: bold;
        text-transform: uppercase;
        text-align: center;
        font-size: ${pxToEm(32, 16)}rem;
      }

      svg {
        z-index: -1;
        position: absolute;
        top: 0;
        height: 100%;
        width: calc(100% + 2em);
        bottom: 0;
      }
    }
  `;

  const toggleValidationBox = () => {
    setOpen(!open);
  };

  const handleValidate = () => {
    setPopupOpen(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
  };

  const confirmValidate = () => {
    setPopupOpen(false);
    handleValidateTicket();
    toggleValidationBox();
  };

  const validationMessage =
    availiableEntries === 1
      ? `${t("general.validate_last_ticket_confirm")}.`
      : availiableEntries && availiableEntries > 1
      ? `${t("general.validate_ticket_confirm")} ${
          availiableEntries === 2
            ? `${availiableEntries - 1} ${t("general.entry")}`
            : `${availiableEntries - 1} ${t("general.entries")}`
        } ${t("general.remains")}.`
      : "";

  return (
    <ValidateBoxView>
      <div className="box-header" onClick={toggleValidationBox}>
        {!open && (
          <>
            <span className="emoji">{"🗝"}</span>
            <p className="text cfl">{t("general.validation_intro")}</p>
          </>
        )}
      </div>

      <button
        data-testid="expand-validate-box"
        className="expand-button"
        onClick={toggleValidationBox}
      >
        <Icon name="arrow-down" />
      </button>
      {open && (
        <>
          <Icon className="logo" name="logo" />
          <p className="cfl validate-note">{`${t(
            "general.ticket_validation_note_start"
          )} ${`${serviceProvider}`}${
            ticketType === "GrantedTicket" &&
            serviceProvider &&
            serviceProvider !== `${myMunicipality} Kommune`
              ? `, ${myMunicipality && myMunicipality} ${t(
                  "marketplace.municipality"
                )}`
              : ""
          } ${t("general.ticket_validation_note_end")}. ${
            code ? `${t("general.validate_note_code")}. ` : ""
          }${t("general.validate_on_entrance")}.`}</p>
          {unlimitedEntries ? (
            <div className="pass">
              <Icon name="pass" />
              <p>{t("general.passcard")}</p>
            </div>
          ) : (
            <>
              {code && <p className="code">{code}</p>}
              <Button
                data-testid="validate-ticket"
                onClick={handleValidate}
                className="validate cfl"
              >
                {t("general.validate")}
              </Button>
            </>
          )}
          <p className="note">
            <span>🗝</span>
            {t("general.inspector_note")}
          </p>
        </>
      )}
      <ConfirmationPopup
        title={t("general.confirm_validation")}
        subtitle={validationMessage}
        open={popupOpen}
        idPrefix="ticket-validate-popup"
        okButtonName={t("general.confirm")}
        cancelButtonName={t("general.cancel")}
        onCancel={closePopup}
        onConfirmClick={confirmValidate}
      />
    </ValidateBoxView>
  );
};

export default ValidateBox;
